import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { TemplateBlockInstance } from '@/templates/domain/types';
import type { DocumentContext } from '../domain/DocumentContext';
import type { BlockContent, BlockStyle } from '@/blocks/domain/types';
import type { Renderer } from '../domain/RendererContract';
import type { BlockKind } from '@/blocks/domain/types/BlockKind';
import { RENDERER_REGISTRY } from '../presentation/blocks/__registry';
import { BlockRenderErrorBoundary } from '../presentation/BlockRenderErrorBoundary';
import { MeasurementPassScope } from './measurementPassContext';

type ImportedBlockJson = { content: BlockContent; style: BlockStyle };

export function useBlockHeightMeasurement(
  instances: TemplateBlockInstance[],
  options: {
    context: DocumentContext;
    accentColor?: string;
    knownPaths?: ReadonlySet<string>;
    /** Match page content width so wrapping matches the real page (avoids under-measured heights). */
    contentWidthPx?: number;
    rendererRegistry?: Partial<Record<BlockKind, Renderer>>;
    deps?: unknown[];
  }
): { heights: Map<string, number>; measurementPass: ReactElement | null } {
  const { context, accentColor, knownPaths, contentWidthPx, deps = [], rendererRegistry = RENDERER_REGISTRY } = options;
  const wrapperRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [heights, setHeights] = useState<Map<string, number>>(() => new Map());
  const containerRef = useRef<HTMLDivElement>(null);
  // A web font or a picture that finishes loading after the first measure changes the text's width or the block's height:
  // measure again, or the page breaks stay computed from the fallback font / the empty picture.
  const [reflow, setReflow] = useState(0);
  const hasInstances = instances.length > 0;
  useEffect(() => {
    const bump = () => setReflow((count) => count + 1);
    const fonts = typeof document === 'undefined' ? undefined : document.fonts;
    fonts?.addEventListener?.('loadingdone', bump);
    // `load` does not bubble: listen while it travels down.
    const container = containerRef.current;
    container?.addEventListener('load', bump, true);
    return () => {
      fonts?.removeEventListener?.('loadingdone', bump);
      container?.removeEventListener('load', bump, true);
    };
  }, [hasInstances]);

  const areMapsEqual = (a: Map<string, number>, b: Map<string, number>) => {
    if (a.size !== b.size) return false;
    for (const [key, value] of a) {
      if (b.get(key) !== value) return false;
    }
    return true;
  };

  const measurementPass = useMemo(() => {
    if (instances.length === 0) return null;

    return (
      <div
        ref={containerRef}
        style={{
          position: 'absolute',
          inset: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
          height: 0,
          overflow: 'hidden',
          width: contentWidthPx && contentWidthPx > 0 ? contentWidthPx : undefined,
          // Match PageRender. The CRM shell body leading is an absolute 24px;
          // measuring against it allocates legal text onto extra pages.
          lineHeight: 1.15
        }}
        aria-hidden="true"
      >
        <MeasurementPassScope>
          {instances.map((instance) => {
            const json = instance.importedBlockJson as ImportedBlockJson | null;
            if (!json) return <div key={instance.templateBlockInstanceId} />;

            const renderer = rendererRegistry[json.content.kind as keyof typeof rendererRegistry];
            if (!renderer) return <div key={instance.templateBlockInstanceId} />;

            const Comp = renderer.Component;
            return (
              <div
                key={instance.templateBlockInstanceId}
                style={{ width: '100%', boxSizing: 'border-box' }}
                ref={(el) => {
                  if (!el) wrapperRefs.current.delete(instance.templateBlockInstanceId);
                  else wrapperRefs.current.set(instance.templateBlockInstanceId, el);
                }}
              >
                <BlockRenderErrorBoundary instanceId={instance.templateBlockInstanceId} blockKind={json.content.kind}>
                  <Comp content={json.content} style={json.style} context={context} accentColor={accentColor} knownPaths={knownPaths} />
                </BlockRenderErrorBoundary>
              </div>
            );
          })}
        </MeasurementPassScope>
      </div>
    );
  }, [accentColor, contentWidthPx, context, instances, knownPaths, rendererRegistry]);

  useLayoutEffect(() => {
    const next = new Map<string, number>();

    for (const instance of instances) {
      const wrapper = wrapperRefs.current.get(instance.templateBlockInstanceId);
      if (!wrapper) continue;

      // Prefer real measurement; fall back to inline minHeight for jsdom/tests.
      const blockEl = wrapper.querySelector('[aria-label^="block-"]') as HTMLElement | null;
      const rectHeight = blockEl?.getBoundingClientRect().height ?? 0;
      const parsedMinHeight = blockEl?.style?.minHeight ? parseFloat(blockEl.style.minHeight) : 0;

      next.set(instance.templateBlockInstanceId, rectHeight || parsedMinHeight || 0);
    }

    // Guard against infinite update loops: only update state when measurement values change.
    setHeights((prev) => (areMapsEqual(prev, next) ? prev : next));
  }, [measurementPass, reflow, ...(deps ?? [])]);

  return { heights, measurementPass };
}
